import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Loader2, FileText, ArrowRight, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import { trackEvent } from "@/components/Analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvoicePay() {
  const { invoiceNumber } = useParams();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/invoices/${invoiceNumber}`)
      .then((r) => mounted && setInv(r.data))
      .catch((e) => mounted && setErr(e.response?.data?.detail || "Invoice not found"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [invoiceNumber]);

  async function pay() {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/invoices/${invoiceNumber}/checkout`);
      trackEvent("invoice_pay_start", {
        invoice_number: invoiceNumber,
        value: inv?.total || 0,
        currency: "GBP",
      });
      if (data.checkout_url) {
        if (data.note) {
          alert(data.note);
        }
        window.location.href = data.checkout_url;
      } else {
        setErr("Could not start payment. Please contact Park Home Support.");
      }
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not start payment");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="section-pad bg-[#F7F4ED] min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#064E2E]" size={32} />
      </div>
    );
  }

  if (err || !inv) {
    return (
      <div className="section-pad bg-[#F7F4ED] min-h-[60vh]" data-testid="invoice-error">
        <SEO title="Invoice — Park Home Support" />
        <div className="container-phs max-w-2xl">
          <div className="card-default text-center">
            <AlertCircle className="text-[#DC2626] mx-auto" size={40} />
            <h1 className="font-display text-2xl mt-4 text-[#071A33]">We couldn&apos;t find that invoice.</h1>
            <p className="mt-3 text-[#475569]">{err || "Please check the invoice number and try again."}</p>
            <a href="/pay" className="btn-outline mt-6 inline-flex">Lookup another invoice</a>
          </div>
        </div>
      </div>
    );
  }

  const paid = inv.status === "PAID";
  const cancelled = inv.status === "CANCELLED";

  return (
    <div data-testid="invoice-pay-page">
      <SEO title={`Pay invoice ${inv.invoice_number} — Park Home Support`} description="Secure invoice payment via Stripe." />
      <section className="section-pad bg-[#F7F4ED] min-h-[60vh]">
        <div className="container-phs max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <FileText className="text-[#064E2E]" size={20} />
            <span className="eyebrow">Park Home Support — Invoice</span>
          </div>
          <h1 className="font-display text-4xl text-[#071A33] leading-tight">
            Invoice <span className="tabular-nums">{inv.invoice_number}</span>
          </h1>

          <div className="card-default mt-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="eyebrow text-[#475569] text-[10px]">Billed to</p>
                <p className="font-medium text-[#071A33] mt-1">{inv.customer_name}</p>
                {inv.due_date && <p className="text-sm text-[#475569] mt-1">Due: {inv.due_date}</p>}
              </div>
              <div className="text-right">
                <p className="eyebrow text-[#475569] text-[10px]">Total due</p>
                <p className="font-display text-4xl text-[#071A33] tabular-nums">
                  <span className="text-xl align-top mr-0.5">£</span>{Number(inv.total).toFixed(2)}
                </p>
                <span className={`mt-2 inline-block px-2.5 py-1 rounded-full text-xs font-medium ${
                  paid ? "bg-[#064E2E] text-white" :
                  cancelled ? "bg-[#FCA5A5]/30 text-[#DC2626]" :
                  "bg-[#0F766E]/15 text-[#0F766E]"
                }`}>{inv.status}</span>
              </div>
            </div>

            <div className="mt-6 border-t border-[#E2DCD0] pt-4">
              <table className="w-full text-sm">
                <thead className="text-[#475569] text-xs uppercase tracking-[0.18em]">
                  <tr>
                    <th className="text-left py-2">Description</th>
                    <th className="text-right py-2 w-20">Qty</th>
                    <th className="text-right py-2 w-28">Unit</th>
                    <th className="text-right py-2 w-28">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2DCD0]">
                  {inv.line_items.map((l, i) => (
                    <tr key={i}>
                      <td className="py-3 text-[#071A33]">{l.description}</td>
                      <td className="py-3 text-right tabular-nums text-[#475569]">{l.quantity}</td>
                      <td className="py-3 text-right tabular-nums text-[#475569]">£{Number(l.unit_price).toFixed(2)}</td>
                      <td className="py-3 text-right tabular-nums text-[#071A33] font-medium">£{(l.quantity * l.unit_price).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-[#475569] text-sm">
                    <td colSpan={3} className="py-1 text-right">Subtotal</td>
                    <td className="py-1 text-right tabular-nums">£{Number(inv.subtotal).toFixed(2)}</td>
                  </tr>
                  {inv.vat > 0 && (
                    <tr className="text-[#475569] text-sm">
                      <td colSpan={3} className="py-1 text-right">VAT ({(inv.vat_rate * 100).toFixed(0)}%)</td>
                      <td className="py-1 text-right tabular-nums">£{Number(inv.vat).toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="text-[#071A33] font-semibold border-t border-[#E2DCD0]">
                    <td colSpan={3} className="pt-3 text-right">Total</td>
                    <td className="pt-3 text-right tabular-nums">£{Number(inv.total).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {inv.notes && (
              <div className="mt-5 bg-[#F7F4ED] rounded-xl p-4">
                <p className="eyebrow text-[#475569] text-[10px] mb-1">Notes</p>
                <p className="text-sm text-[#475569] whitespace-pre-wrap">{inv.notes}</p>
              </div>
            )}

            {paid && (
              <div className="mt-6 flex items-center gap-3 text-[#064E2E]" data-testid="invoice-paid-state">
                <CheckCircle2 size={24} />
                <p className="font-medium">This invoice has been paid. Thank you.</p>
              </div>
            )}
            {cancelled && (
              <div className="mt-6 flex items-center gap-3 text-[#DC2626]">
                <AlertCircle size={24} />
                <p className="font-medium">This invoice has been cancelled.</p>
              </div>
            )}
            {!paid && !cancelled && (
              <button
                onClick={pay}
                disabled={busy}
                className="btn-primary mt-7 w-full sm:w-auto text-base"
                data-testid="invoice-pay-btn"
              >
                {busy ? <><Loader2 className="animate-spin" size={18}/> Starting Stripe…</> : <>Pay £{Number(inv.total).toFixed(2)} securely <ArrowRight size={18} /></>}
              </button>
            )}

            <p className="mt-4 text-xs text-[#94A3B8] flex items-center gap-1.5">
              <ShieldCheck size={12} /> Secure payment processed by Stripe. We don&apos;t see or store your card details.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
